import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { citasApi } from '../services/api';
import type { Cita } from '../types/api';

export interface TodayAppointment {
  id: string;
  patientName: string;
  time: string;
  timeStart: string;
  timeEnd: string;
  status: 'PENDIENTE' | 'CONFIRMADA' | 'REALIZADA' | 'CANCELADA' | 'NO_ACUDE' | 'REPROGRAMADA';
  professionalName: string;
  treatmentName: string;
  notes?: string;
  isNext: boolean;
}

export interface TodayAppointmentStats {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  nextAppointment?: TodayAppointment;
}

export const useTodayAppointments = () => {
  const queryClient = useQueryClient();
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]);
  const [stats, setStats] = useState<TodayAppointmentStats>({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0
  });

  // Obtener fecha de hoy en formato YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];

  const { data: citas, isLoading, error, refetch } = useQuery({
    queryKey: ['citas-hoy', today],
    queryFn: async () => {
      try {
        const response = await citasApi.getAll({
          fecha_inicio: today,
          fecha_fin: today
        });
        return response;
      } catch (error) {
        console.error('Error fetching today appointments:', error);
        return [];
      }
    },
    refetchInterval: 5 * 60 * 1000, // Refrescar cada 5 minutos
    staleTime: 2 * 60 * 1000, // Considerar datos obsoletos después de 2 minutos
  });

  // Funciones para cambiar estados
  const updateAppointmentStatus = async (appointmentId: string, newStatus: TodayAppointment['status'], motivo?: string) => {
    try {
      const id = parseInt(appointmentId);
      
      if (newStatus === 'REALIZADA') {
        await citasApi.completar(id);
      } else if (newStatus === 'CANCELADA') {
        await citasApi.cancelar(id, motivo || 'Cancelada desde dashboard');
      } else {
        // Para pendiente y confirmada, usar la función update general
        await citasApi.update(id, { estado: newStatus });
      }

      // Actualizar estado local inmediatamente
      setTodayAppointments(prev => prev.map(apt => 
        apt.id === appointmentId 
          ? { ...apt, status: newStatus }
          : apt
      ));

      // Refrescar datos del servidor
      setTimeout(() => {
        refetch();
        // También refrescar otras queries relacionadas si existen
        queryClient.invalidateQueries({ queryKey: ['citas'] });
        queryClient.invalidateQueries({ queryKey: ['estadisticas'] });
      }, 500);

      return true;
    } catch (error) {
      console.error('Error updating appointment status:', error);
      // Revertir cambio local si falla
      refetch();
      throw error;
    }
  };

  const markAsCompleted = (appointmentId: string) => {
    return updateAppointmentStatus(appointmentId, 'REALIZADA');
  };

  const markAsCancelled = (appointmentId: string, motivo?: string) => {
    return updateAppointmentStatus(appointmentId, 'CANCELADA', motivo);
  };

  const markAsConfirmed = (appointmentId: string) => {
    return updateAppointmentStatus(appointmentId, 'CONFIRMADA');
  };

  const markAsPending = (appointmentId: string) => {
    return updateAppointmentStatus(appointmentId, 'PENDIENTE');
  };

  useEffect(() => {
    if (citas && Array.isArray(citas)) {
      const currentTime = new Date();
      
      // Mapear citas a nuestro formato
      const mappedAppointments: TodayAppointment[] = citas
        .filter(cita => cita.fecha === today) // Filtro adicional por si acaso
        .map(cita => {
          // Crear fecha completa para comparación
          const appointmentDateTime = new Date(`${cita.fecha}T${cita.hora_inicio}`);
          
          return {
            id: cita.id.toString(),
            patientName: cita.paciente 
              ? `${cita.paciente.nombre} ${cita.paciente.apellidos || ''}`.trim()
              : 'Paciente no especificado',
            time: `${cita.hora_inicio.substring(0, 5)} - ${cita.hora_fin.substring(0, 5)}`,
            timeStart: cita.hora_inicio,
            timeEnd: cita.hora_fin,
            status: cita.estado,
            professionalName: cita.profesional
              ? `${cita.profesional.nombre} ${cita.profesional.apellidos || ''}`.trim()
              : 'Profesional no especificado',
            treatmentName: cita.tratamiento?.nombre || 'Tratamiento no especificado',
            notes: cita.observaciones,
            isNext: false, // Se asignará después
            appointmentDateTime // Campo temporal para ordenamiento
          } as TodayAppointment & { appointmentDateTime: Date };
        })
        .sort((a, b) => {
          // Ordenar por hora de inicio
          return a.timeStart.localeCompare(b.timeStart);
        });

      // Encontrar la próxima cita (la primera que no haya pasado y no esté cancelada/completada)
      let nextAppointmentIndex = -1;
      for (let i = 0; i < mappedAppointments.length; i++) {
        const apt = mappedAppointments[i] as TodayAppointment & { appointmentDateTime: Date };
        if (
          apt.appointmentDateTime > currentTime && 
          apt.status !== 'CANCELADA' && 
          apt.status !== 'REALIZADA'
        ) {
          nextAppointmentIndex = i;
          break;
        }
      }

      // Marcar la próxima cita
      if (nextAppointmentIndex !== -1) {
        mappedAppointments[nextAppointmentIndex].isNext = true;
      }

      // Limpiar el campo temporal
      const cleanedAppointments = mappedAppointments.map(apt => {
        const { appointmentDateTime, ...cleanApt } = apt as any;
        return cleanApt;
      });

      setTodayAppointments(cleanedAppointments);

      // Calcular estadísticas
      const newStats: TodayAppointmentStats = {
        total: cleanedAppointments.length,
        pending: cleanedAppointments.filter(apt => apt.status === 'PENDIENTE').length,
        confirmed: cleanedAppointments.filter(apt => apt.status === 'CONFIRMADA').length,
        completed: cleanedAppointments.filter(apt => apt.status === 'REALIZADA').length,
        cancelled: cleanedAppointments.filter(apt => apt.status === 'CANCELADA').length,
        nextAppointment: cleanedAppointments.find(apt => apt.isNext)
      };

      setStats(newStats);
    } else {
      setTodayAppointments([]);
      setStats({
        total: 0,
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
      });
    }
  }, [citas, today]);

  return {
    appointments: todayAppointments,
    stats,
    isLoading,
    error,
    refetch,
    // Funciones para cambiar estados
    updateAppointmentStatus,
    markAsCompleted,
    markAsCancelled,
    markAsConfirmed,
    markAsPending
  };
}; 